import { useLayoutEffect, useRef, useState } from 'react';
import Skeleton from '@/common/components/skeleton/Skeleton';
import { bindVideoReadyListeners, playVideoSafely } from '@/common/functions/videoElement';
import {
	ChatMediaOverlay,
	ChatMediaOverlayCopy,
	ChatMediaOverlayTitle,
	ChatMediaSkeleton,
	ChatVideoElement,
	ChatVideoPlayHint,
	ChatVideoShell,
} from './chatMedia.styles';

const ChatVideoPlayer = ({
	src,
	isLoading = false,
	hasError = false,
	errorText,
}) => {
	const videoRef = useRef(null);
	const [isReady, setIsReady] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [playbackError, setPlaybackError] = useState(false);

	const showError = hasError || playbackError;

	useLayoutEffect(() => {
		setIsReady(false);
		setIsPlaying(false);
		setPlaybackError(false);
	}, [src]);

	useLayoutEffect(() => {
		if (!src) {
			return undefined;
		}

		const video = videoRef.current;

		if (!video) {
			return undefined;
		}

		return bindVideoReadyListeners(
			video,
			() => setIsReady(true),
			() => setPlaybackError(true),
		);
	}, [src]);

	const handleTogglePlayback = () => {
		const video = videoRef.current;

		if (!video || !isReady || showError) {
			return;
		}

		if (video.paused) {
			if (video.ended) {
				video.currentTime = 0;
			}
			playVideoSafely(video);
			return;
		}

		video.pause();
		setIsPlaying(false);
	};

	const showSkeleton = !showError && (isLoading || !src || !isReady);

	return (
		<ChatVideoShell
			onClick={handleTogglePlayback}
			role="presentation"
			$isPlaying={isPlaying && isReady && !showError}
		>
			{showSkeleton && (
				<ChatMediaSkeleton aria-hidden="true">
					<Skeleton
						width="100%"
						height="100%"
						radius="0"
						minWidth="100%"
						minHeight="100%"
					/>
				</ChatMediaSkeleton>
			)}
			{showError && (
				<ChatMediaOverlay aria-hidden="false">
					<ChatMediaOverlayCopy>
						<ChatMediaOverlayTitle>{errorText}</ChatMediaOverlayTitle>
					</ChatMediaOverlayCopy>
				</ChatMediaOverlay>
			)}
			{src ? (
				<ChatVideoElement
					ref={videoRef}
					key={src}
					src={src}
					muted
					playsInline
					preload="auto"
					$isReady={isReady && !showError}
					onPlay={() => setIsPlaying(true)}
					onPause={() => setIsPlaying(false)}
					onEnded={() => setIsPlaying(false)}
				/>
			) : null}
			{isReady && !isPlaying && !showError ? (
				<ChatVideoPlayHint $visible aria-hidden="true">
					<span>▶</span>
				</ChatVideoPlayHint>
			) : null}
		</ChatVideoShell>
	);
};

export default ChatVideoPlayer;
